/**
 * Album pricing curve — display only.
 *
 * THESE NUMBERS ARE NOT MERCHANT-EDITABLE, AND THAT IS DELIBERATE.
 *
 * They mirror the 40 real Shopify variants in docs/product-spec.md. If a
 * merchant could change them from the theme editor, the homepage could quote a
 * rate the cart will not honour. To change a price: change the product
 * catalogue and docs/product-spec.md, then tests/fixtures/album-list-prices.json,
 * then this file, then run the tests.
 *
 *   ./docs/test.sh          (was documented as `node --test theme/tests/`, which
 *                            is not where the tests live)
 *
 * Nothing here touches the cart. Resolving a variant ID and building the cart
 * payload belong to rf-order-payload.js; the fetch calls belong to
 * rf-order-cart.js. This file is pure arithmetic, testable under plain node.
 *
 * Wrapped so the same file serves as a Shopify theme asset (exposing
 * window.PhotokivePricing) and as a CommonJS module for the test run. No build
 * step and no bundler.
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.PhotokivePricing = api;
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /** Rate for a single album, and the ceiling savings are measured against. */
  var BASE_RATE = 129;
  /** Rate at and above TIER_TOP albums. */
  var FLOOR_RATE = 79;
  /** The album count at which the curve flattens. */
  var TIER_TOP = 20;
  /**
   * HOW MANY LOOSE PHOTOS ARE IN ONE SET. The unit the whole loose-photo half
   * of the catalogue is denominated in: a customer buys SETS, and this is what
   * a set is.
   *
   * ⚠ IT IS NOT A PRICE, A RATE OR A CONVERSION FACTOR, and the name says so
   * because there is a second 100 in this file that IS one:
   * `LOOSE_SET_CENTS = LOOSE_SET_PRICE * 100` in the order-builder block below
   * converts DOLLARS TO CENTS. The two hundreds are unrelated and always have
   * been -- they would still be unrelated if the set size were 250 or the
   * currency had no minor unit -- so neither may ever be written in terms of
   * the other, however tempting the arithmetic looks.
   *
   * ⚠ THE DOCBLOCK THAT SAID THIS SENTENCE USED TO SIT ABOVE `LOOSE_SET_PRICE`,
   * where it read as context for the price rather than as a description of
   * anything, and there was no constant for the number it named. The 100 was
   * implicit in every comment, every setting default and every summary string
   * in the theme and declared nowhere. Corrected 19 Sep 2026, when the
   * loose-photo cap's message needed to STATE it.
   *
   * Nothing prices from it: the set price is flat per set, so no arithmetic in
   * this module multiplies by it. It exists so the copy that quotes a photo
   * count derives that count instead of hardcoding one -- see the cap message
   * in sections/order-builder.liquid, filled by render() in
   * assets/rf-order-builder.js.
   */
  var LOOSE_SET_PHOTOS = 100;
  /** What ONE set of loose photos costs, in whole dollars. */
  var LOOSE_SET_PRICE = 29;

  /**
   * The body the three named clamps share: floor to a whole number, and send
   * junk -- and anything below `floor` -- to `floor` rather than propagate NaN
   * into a price the customer sees. clampAlbums, clampSets and orderAlbums
   * delegate to it and differ in nothing but that floor. See the
   * ONE CLAMP WITH TWO FLOORS block below.
   */
  function clampCount(value, floor) {
    var n = Math.floor(Number(value));
    if (!isFinite(n) || n < floor) return floor;
    return n;
  }

  /** The pricing page's album count: a whole number, at least 1. */
  function clampAlbums(value) {
    return clampCount(value, 1);
  }

  /**
   * Per-album rate in whole dollars. A straight line from BASE_RATE at one
   * album to FLOOR_RATE at TIER_TOP, flat above that.
   *
   * A smooth curve, not tiers — every one of the 20 points is a real variant.
   */
  function perAlbum(count) {
    var n = clampAlbums(count);
    if (n >= TIER_TOP) return FLOOR_RATE;
    return Math.round(BASE_RATE - ((BASE_RATE - FLOOR_RATE) * (n - 1)) / (TIER_TOP - 1));
  }

  function albumTotal(count) {
    var n = clampAlbums(count);
    return perAlbum(n) * n;
  }

  /** Saved against paying the single-album rate for every album. */
  function savedTotal(count) {
    var n = clampAlbums(count);
    return (BASE_RATE - perAlbum(n)) * n;
  }

  function savedPercent(count) {
    return Math.round(((BASE_RATE - perAlbum(count)) / BASE_RATE) * 100);
  }

  function looseSetPrice() {
    return LOOSE_SET_PRICE;
  }

  function looseTotal(sets) {
    var n = Math.floor(Number(sets));
    if (!isFinite(n) || n < 0) return 0;
    return LOOSE_SET_PRICE * n;
  }

  /**
   * Whole dollars, thousands separated, no cents. Correct for the per-album
   * RATES this was written for, every one of which is a whole dollar.
   *
   * DISPLAY ONLY. NEVER use it for a figure that is charged, asserted, or written
   * to a cart attribute — it cannot express an order total:
   *
   *   money(678.60) -> "$679"      the cents are gone
   *   money(1896)   -> "$1,896"    strip the $ and parseFloat gives 1,
   *                                a 99.95% under-invoice
   *
   * That is the same `parseInt("1,000") === 1` failure the builder's input path
   * guards against. Money in attributes is bare integer cents — see
   * docs/product-spec.md §9 and the `*Cents` functions below.
   */
  function money(value) {
    return '$' + Math.round(Number(value) || 0).toLocaleString('en-US');
  }

  /** Agrees in number with the raw count, so 0 reads "albums". */
  function albumWord(count) {
    return Number(count) === 1 ? 'album' : 'albums';
  }

  /* ================================================================== *
   * ORDER BUILDER — added 23 Aug 2026
   *
   * Everything above serves the pricing-page calculator and predates the
   * builder. Nothing below changes what it computes: `clampAlbums` and
   * `perAlbum` in particular are pinned by tests/album-calculator.test.js and
   * are shared with a shipped page. `clampAlbums` is a one-line delegation to
   * `clampCount` -- a refactor that moved no returned value, and that test file
   * passed across it with zero edits.
   *
   * Still nothing here touches the cart or the DOM. Variant IDs and the cart
   * payload live in rf-order-payload.js; the fetch calls live in
   * rf-order-cart.js. That split is what keeps the dangerous logic testable
   * under plain node.
   *
   * ALL MONEY BELOW IS INTEGER CENTS. Floats are never used for money:
   * (8.165).toFixed(2) is "8.16", because toFixed rounds the binary value.
   * ================================================================== */

  /** The USB is the only non-clean list price in the catalogue. */
  var USB_LIST_CENTS = 2499;
  /**
   * One set of loose photos, in cents.
   *
   * ⚠ THIS `100` IS DOLLARS-TO-CENTS AND HAS NOTHING TO DO WITH LOOSE_SET_PHOTOS,
   * which is also 100 and is declared at the top of this file. They agree by
   * coincidence -- a currency's minor unit and a product's pack size -- and a
   * reader who folds one into the other gets a set price that moves when the
   * pack size changes. Neither is ever written in terms of the other. The long
   * form of this warning is at LOOSE_SET_PHOTOS.
   */
  var LOOSE_SET_CENTS = LOOSE_SET_PRICE * 100;
  /**
   * ONE CLAMP WITH TWO FLOORS -- AND parseCount, WHICH IS THE DANGEROUS ONE.
   *
   * clampCount(value, floor) is the single body: clampAlbums (floor 1, shared
   * with the shipped pricing page), clampSets and orderAlbums (floor 0) are
   * one-line delegations, so a floor is all that separates them. parseCount is
   * NOT one of them -- it does the same Math.floor(Number(v)) and ALSO strips
   * [\s,_] first, so on a separator-bearing string it disagrees with every
   * clamp: clampAlbums('1,000') returns 1 while parseCount('1,000') returns
   * 1000. Note the 1, not NaN -- a silently plausible number is worse than an
   * obviously broken one, because nothing downstream can tell it apart from a
   * customer who asked for one album. The stripping stays out of clampCount
   * because clampAlbums is pinned by tests/album-calculator.test.js.
   *
   * So: NEVER pass user input straight to a clamp. Use parseCount, which is what
   * rf-order-builder.js's field, stepper and ?albums= paths do. The clamps are
   * for values already known to be bare integers.
   */

  /**
   * TWO SELF-SERVE CEILINGS, ONE PER COUNT. Above either, a conversation.
   *
   * THEY NO LONGER HOLD THE SAME VALUE. The previous unit split one shared
   * constant into two names at 50 apiece so that this one could move exactly
   * one of them: the PHOTO-SET ceiling is now 100 and the ALBUM ceiling is
   * still 50. Deleting the shared name rather than keeping it as "the album
   * one" is what forced each site to be visited -- a surviving name would have
   * kept compiling at the sets comparison while measuring it against the album
   * ceiling, which is green and wrong, and is exactly the defect that would
   * have shipped on the day these two values diverged.
   *
   * So: never reach for "the ceiling". It is now ambiguous in a way that
   * changes verdicts. Name the count you are asking about.
   *
   * WHY 100 SETS, AND WHY THE ASYMMETRY IS DELIBERATE. The 50 was a money and
   * junk guard, never a fulfilment limit -- an unbounded sets field put 9,999
   * sets and a $217k balance attribute within reach. Kurtis confirmed on
   * 19 Sep 2026 that a 100-set order (10,000 loose photos, about $2,900) is
   * operationally fine without an intake conversation. That breaks the
   * both-counts-share-one-ceiling symmetry of the order-builder plan's D6 on
   * purpose: a customer sending 30 albums and 80 sets is routed to a
   * conversation for the albums and not for the sets. He was asked about the
   * 100-set case and answered that; he did not separately argue the asymmetry,
   * so it is a consequence he accepted rather than a case he made. If it ever
   * reads wrong to a customer, re-read D6 in
   * docs/plans/2026-08-22-001-feat-order-builder-plan.md.
   *
   * ⚠ THIS IS STILL THE BACKSTOP, NOT THE CUSTOMER-FACING CAP. Nothing here
   * stops 101 being typed into the field; it stops the order being sold. The
   * entry cap that clamps the field and says why is a later unit.
   */
  /**
   * ⚠ THESE TWO ARE THE SHIPPED DEFAULTS, NOT THE LIMITS. Since 19 Sep 2026 both
   * ceilings are merchant-editable -- `max_self_serve_albums` and
   * `max_self_serve_sets` (Theme settings -> Order rules) -- and they reach this
   * module as an argument, through the variant map, never by injection. See
   * selfServeLimits() below for the transport and why it is the map.
   *
   * What these names hold now is the value used when the page could not tell us
   * one: no map at all, no key in the map, or a value the map could not carry.
   * They are still exported, and a test still asserts them, because "what the
   * theme falls back to" is a fact worth pinning -- but a reader asking "what is
   * the limit on THIS page" must ask selfServeLimits(map), not these.
   */
  /** Shipped fallback for the ALBUM ceiling. */
  var MAX_SELF_SERVE_ALBUMS = 50;
  /** Shipped fallback for the PHOTO-SET ceiling. Raised from 50, 19 Sep 2026. */
  var MAX_SELF_SERVE_SETS = 100;

  /**
   * Payment-mode factors as integer numerator/denominator, deliberately.
   * `cents * 9 / 10` keeps the intermediate exact where `cents * 0.9` would not:
   * 12900 * 0.9 is representable, but the general case is not, and a float that
   * lands a hair below a .5 boundary rounds the wrong way.
   */
  var MODE_FACTORS = {
    full: { num: 9, den: 10 },
    deposit: { num: 25, den: 100 },
  };

  function modeFactor(mode) {
    var f = MODE_FACTORS[mode];
    if (!f) throw new Error('unknown payment mode: ' + mode);
    return f;
  }

  /**
   * Round half AWAY FROM ZERO, which is what docs/product-spec.md §3 specifies.
   * Math.round is half-up, so it differs on negatives (Math.round(-2.5) is -2).
   * No price here is negative, but a discount subtraction could be, and relying
   * on "we never hit that" is how the wrong rounding mode ships.
   */
  function roundHalfAwayFromZero(x) {
    return x < 0 ? -Math.round(-x) : Math.round(x);
  }

  /**
   * Which of the 20 curve points applies — i.e. the `Albums` option value on the
   * variant. NOT the same question as `perAlbum`, which is flat above TIER_TOP
   * rather than capped: perAlbum(500) is 79 while curvePointFor(500) is 20.
   * Option value 20 means "the 20-or-more rate", not "exactly 20".
   */
  function curvePointFor(count) {
    return Math.min(clampAlbums(count), TIER_TOP);
  }

  /** Photo-set count. Floor 0 -- an order may carry no photo sets. */
  function clampSets(value) {
    return clampCount(value, 0);
  }

  /**
   * The album count the ORDER BUILDER prices from. Floor 0, and the floor is
   * UNCONDITIONAL -- this file reads no merchant setting and must not start.
   *
   * Two reasons, and the second outlived the first. While
   * `allow_zero_album_orders` is on, an order may carry photo sets and no albums
   * at all, where clampAlbums's floor of 1 silently bills one album at the
   * one-album rate. While it is off that order is refused -- but the refusal is
   * `albums === 0`, tested in rf-order-payload.js against a count that reached it
   * through a floor of 0. A floor of 1 here would make the zero unobservable and
   * the guard unable to fire, so 0 is now load-bearing in BOTH positions.
   *
   * Same floor as clampSets, and kept as its own name because a call site
   * reading `clampSets(state.albums)` would misname the count it is parsing.
   */
  function orderAlbums(value) {
    return clampCount(value, 0);
  }

  /**
   * Parse a count out of a text field or a URL parameter.
   *
   * Separator stripping lives HERE and not in clampAlbums, which is shared with
   * the pricing page: `parseInt("1,000")` is 1, a silent 1000x undercount, so an
   * input path must strip separators — but a URL handoff also reaches
   * clampAlbums, and there clamping junk to the floor is the safer behaviour.
   *
   * Caps at MAX_SAFE_INTEGER so an exponential-notation float can never enter the
   * cent arithmetic. The order is refused by exceedsSelfServe long before that
   * matters; this is the second line of defence, not the first.
   */
  function parseCount(value, floor) {
    var min = Number(floor) || 0;
    var cleaned = String(value == null ? '' : value).replace(/[\s,_]/g, '');
    var n = Math.floor(Number(cleaned));
    if (!isFinite(n) || n < min) return min;
    if (n > Number.MAX_SAFE_INTEGER) return Number.MAX_SAFE_INTEGER;
    return n;
  }

  /** The real per-unit price on the Shopify variant, in cents. */
  function unitPriceCents(listCents, mode) {
    var f = modeFactor(mode);
    return roundHalfAwayFromZero((Number(listCents) * f.num) / f.den);
  }

  /**
   * The list rate for ONE album, in cents. Stays on clampAlbums's floor of 1
   * deliberately: it returns a RATE, not a total, and both callers below
   * multiply it by the album count -- so at zero albums the term is already
   * exactly 0, and a gate here would be a branch with two provably equal arms.
   * It is also exported, and a caller that DISPLAYS the rate has to decide for
   * itself what zero albums means; this function cannot decide it for them.
   */
  function albumListRateCents(albums) {
    return perAlbum(clampAlbums(albums)) * 100;
  }

  /** What the customer is told the order is worth, before any mirror. */
  function listSubtotalCents(state) {
    var a = orderAlbums(state.albums);
    var s = clampSets(state.sets);
    return albumListRateCents(a) * a +
           LOOSE_SET_CENTS * s +
           (state.usb ? USB_LIST_CENTS : 0);
  }

  /**
   * What Shopify will actually charge: the sum of the real, individually rounded
   * variant prices times their quantities. This is the authority.
   */
  function chargedCents(state, mode) {
    var a = orderAlbums(state.albums);
    var s = clampSets(state.sets);
    return unitPriceCents(albumListRateCents(a), mode) * a +
           unitPriceCents(LOOSE_SET_CENTS, mode) * s +
           (state.usb ? unitPriceCents(USB_LIST_CENTS, mode) : 0);
  }

  /**
   * What the builder displays, optionally after a promo code.
   *
   * Without a code this is `round(listSubtotal x factor)` — a single
   * multiplication rather than a sum of rounded lines. That it equals
   * `chargedCents` for every reachable configuration is asserted exhaustively in
   * tests/order-builder.test.js, and it holds because every list price's cents
   * end in 00 or 99, so a half-cent tie is unreachable. That precondition is
   * itself asserted; a $29.99 photo set would break it.
   *
   * `code` is {pct} or {amountCents}. It reduces TODAY'S PAYMENT ONLY — the
   * balance is fixed before it and never moves (photokivepricing.md §2, revised
   * 22 Aug 2026).
   */
  function dueTodayCents(state, mode, code) {
    var f = modeFactor(mode);
    var base = roundHalfAwayFromZero((listSubtotalCents(state) * f.num) / f.den);
    if (!code) return base;
    var after = base;
    if (code.pct != null) {
      after = base - roundHalfAwayFromZero((base * Number(code.pct)) / 100);
    } else if (code.amountCents != null) {
      after = base - Number(code.amountCents);
    }
    // Shopify caps a discount at the order total. MEASURED 2 Sep 2026, not
    // assumed -- this comment was an untested platform claim until then, which
    // is the one thing
    // docs/solutions/best-practices/a-comment-that-names-another-rule-is-an-untested-claim-2026-08-24.md
    // says a platform claim may never be. The observation, from a real
    // order-level $100 code against a one-album deposit cart on the dev store:
    //
    //   before  original_total_price=3225  total_price=3225
    //   after   original_total_price=3225  total_price=0
    //   cart_level_discount_applications[0].total_allocated_amount = 3225
    //
    // Note the allocated amount is 3225, the GRANTED discount, not the code's
    // face value of 10000. Anything deriving a balance from the code's face
    // value rather than the allocation is wrong by the difference.
    // Full record: docs/promo-code-test-2026-09.md.
    return after < 0 ? 0 : after;
  }

  /**
   * The deferred balance. Derived by SUBTRACTION so that
   * `deposit + balance === listSubtotal` holds by construction rather than by
   * luck — rounding both sides independently is the classic penny-allocation bug.
   *
   * Promo-independent by design: it takes no code argument, because the theme
   * writes it before the customer reaches the discount box.
   */
  function balanceCents(state) {
    return listSubtotalCents(state) - chargedCents(state, 'deposit');
  }

  /**
   * Format integer cents for display: whole dollars bare, fractional to 2dp.
   *
   * Matches the design source's own formatter and `money_without_trailing_zeros`,
   * which is what the server-rendered figures use -- so a value bound by
   * JavaScript cannot look different from the same value rendered by Liquid.
   *
   * Takes CENTS, unlike money(), which takes dollars and drops the cents. Cents
   * are divided out only at this final formatting step; nothing upstream of here
   * touches a fractional number.
   */
  function moneyFromCents(cents) {
    var c = Math.round(Number(cents));
    if (!isFinite(c)) c = 0;
    var neg = c < 0;
    if (neg) c = -c;
    var whole = Math.floor(c / 100);
    var rem = c % 100;
    var body = whole.toLocaleString('en-US');
    if (rem !== 0) body += '.' + (rem < 10 ? '0' + rem : String(rem));
    return (neg ? '-$' : '$') + body;
  }

  /**
   * ONE LIMIT, SANITISED, AND THIS IS WHERE THE BAD-VALUE POLICY LIVES.
   *
   * THE POLICY, stated once so the three call sites cannot each invent one: a
   * limit is honoured only when it is a JavaScript NUMBER that is finite and,
   * floored, at least 1. Everything else -- absent, null, a string, NaN,
   * Infinity, 0, negative, a fraction below 1 -- falls back to the shipped
   * default handed in as `fallback`.
   *
   * ⚠ THE DIRECTION OF THE FALLBACK IS THE WHOLE POINT. The tempting reading of
   * a junk limit is "be cautious, use 0" -- and a limit of 0 REFUSES EVERY
   * ORDER, because every sellable order has at least one album or one set. The
   * cautious-looking answer takes the store offline; the shipped default is the
   * value the theme behaved as before anyone edited anything. So junk lands on
   * the shipped number, never on zero.
   *
   * ⚠ AND THAT IS ALSO WHAT COVERS A MISSING config/settings_data.json. The
   * section emits the setting as `{{ settings.max_self_serve_albums | plus: 0 }}`
   * with NO Liquid `default:` filter, deliberately: a `default:` there would put
   * the shipped number in a second file, and two sources for one number is the
   * divergence this module already refuses elsewhere. An unstored range renders
   * blank, `plus: 0` makes that 0, and the `n < 1` arm above turns it back into
   * the shipped value. One number, one place.
   *
   * A STRING IS REJECTED RATHER THAN PARSED, which looks strict and is the
   * point: `| plus: 0` is the only control on a numeric entering the JSON block
   * (sections/order-builder.liquid says why, and docs/audit-render-sites.py
   * structurally cannot see it). If a string ever arrives, the coercion has been
   * removed -- silently accepting `'25'` would make that removal invisible here
   * as well as there.
   */
  function limitOrDefault(value, fallback) {
    if (typeof value !== 'number') return fallback;
    if (!isFinite(value)) return fallback;
    var n = Math.floor(value);
    if (n < 1) return fallback;
    return n;
  }

  /**
   * The two ceilings THIS PAGE is judged against, read off the variant map.
   *
   * WHY THE MAP AND NOT INJECTION -- the plan's D4, in one line each: this module
   * has no DOM surface at all; the two constants are exported BY VALUE, so a
   * post-load mutation would be visible to the closure and invisible to the
   * exported property; and consumers snapshot `pricing` at module-eval time, so
   * there is no window between two deferred scripts to inject in. The map is
   * already the theme's stated transport for behaviour switches -- `allowZeroAlbums`
   * rides there -- and a second transport for a second switch is a second thing
   * to keep in step.
   *
   * ⚠ `map && map.maxSelfServeAlbums` IS THE CANONICAL SPELLING, and it lives
   * HERE rather than at the three call sites for exactly the reason the album
   * switch's spelling is pinned by a test: the `map &&` half is not defensive
   * padding. sections/order-builder.liquid renders the variants JSON inside its
   * `sellable` gate while the two number fields render outside it, so a page with
   * unresolved products ships builder markup with NO MAP AT ALL -- and that page
   * still has to answer "is this order too big". Three copies of the guard is
   * three chances to omit it; one copy is one.
   *
   * Takes a map, returns `{albums, sets}` -- both already sanitised, so a caller
   * may hand the result straight to exceedsSelfServe() or build its own literal.
   */
  function selfServeLimits(map) {
    return {
      albums: limitOrDefault(map && map.maxSelfServeAlbums, MAX_SELF_SERVE_ALBUMS),
      sets: limitOrDefault(map && map.maxSelfServeSets, MAX_SELF_SERVE_SETS)
    };
  }

  /**
   * WHICH ceiling the order broke, or null when it broke neither.
   *
   * ONE COMPARISON, TWO READERS, and that is the whole reason this exists as a
   * function rather than as two expressions. exceedsSelfServe() below wants the
   * verdict; rf-order-payload.js's submit-time refusal wants the NUMBER to put
   * in the message. Written twice, the message would go on quoting the album
   * ceiling for a sets order the moment the two values stop agreeing -- the
   * "two sources for one number" hazard rf-order-payload.js already names about
   * listCents.
   *
   * Reads the album count through orderAlbums for one reason: so the builder
   * never reads the same input at two different floors. The verdict does not
   * move -- neither 0 nor the 1 clampAlbums returns for it is above the album
   * limit, whose smallest editable value is 1.
   *
   * ALBUMS ARE TESTED FIRST, and a state over both ceilings is reported as an
   * album breach. That is the order the page reads its own two fields in, and it
   * is a decision rather than an accident of `||`.
   */
  function selfServeBreach(state, limits) {
    var l = limits || {};
    var albumLimit = limitOrDefault(l.albums, MAX_SELF_SERVE_ALBUMS);
    var setsLimit = limitOrDefault(l.sets, MAX_SELF_SERVE_SETS);
    if (orderAlbums(state.albums) > albumLimit) {
      return { count: 'albums', limit: albumLimit };
    }
    if (clampSets(state.sets) > setsLimit) {
      return { count: 'sets', limit: setsLimit };
    }
    return null;
  }

  /**
   * True when the order is too large to sell without a conversation.
   *
   * TWO ARGUMENTS SINCE 19 Sep 2026, and the second is OPTIONAL BY DESIGN rather
   * than by laziness: omitting it asks the question against the shipped
   * fallbacks, which is the right answer for a caller that has no map and the
   * only answer available to one. Every caller inside this theme passes
   * selfServeLimits(map); a caller that does not is not wrong, it is judging a
   * state rather than a page.
   *
   * ⚠ THREE CALL SITES, AND THEY MUST AGREE. rf-order-builder.js's render()
   * hides the submit with it, rf-order-builder.js's liveSentence() announces
   * from it, and rf-order-cart.js's step 9 refuses the submit with it --
   * rf-order-payload.js reaches selfServeBreach() directly because it wants the
   * NUMBER. A site that forgets the second argument goes on judging against the
   * shipped fallback while the others judge against the merchant's value, and
   * the page then hides a submit that the cart would have accepted, or the
   * reverse. That is the defect this unit was most likely to ship; it is pinned
   * by a test that reads all four files, not by this comment.
   */
  function exceedsSelfServe(state, limits) {
    return selfServeBreach(state, limits) !== null;
  }

  return {
    BASE_RATE: BASE_RATE,
    FLOOR_RATE: FLOOR_RATE,
    TIER_TOP: TIER_TOP,
    clampAlbums: clampAlbums,
    perAlbum: perAlbum,
    albumTotal: albumTotal,
    savedTotal: savedTotal,
    savedPercent: savedPercent,
    looseSetPrice: looseSetPrice,
    looseTotal: looseTotal,
    LOOSE_SET_PHOTOS: LOOSE_SET_PHOTOS,
    money: money,
    albumWord: albumWord,

    /* order builder */
    USB_LIST_CENTS: USB_LIST_CENTS,
    LOOSE_SET_CENTS: LOOSE_SET_CENTS,
    MAX_SELF_SERVE_ALBUMS: MAX_SELF_SERVE_ALBUMS,
    MAX_SELF_SERVE_SETS: MAX_SELF_SERVE_SETS,
    roundHalfAwayFromZero: roundHalfAwayFromZero,
    curvePointFor: curvePointFor,
    clampSets: clampSets,
    orderAlbums: orderAlbums,
    parseCount: parseCount,
    unitPriceCents: unitPriceCents,
    albumListRateCents: albumListRateCents,
    listSubtotalCents: listSubtotalCents,
    chargedCents: chargedCents,
    dueTodayCents: dueTodayCents,
    balanceCents: balanceCents,
    selfServeLimits: selfServeLimits,
    selfServeBreach: selfServeBreach,
    exceedsSelfServe: exceedsSelfServe,
    moneyFromCents: moneyFromCents,
  };
});
